import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";
import { Shimmer } from "../Shimmer/Shimmer";
import { RequestTable } from "../RequestTable/RequestTable";
import { CreateRequestModal } from "../CreateRequestModal/CreateRequestModal";
import type { AdminTabId } from "../AdminWorkspace/adminTabs";
import { TariffAccessBanner } from "../TariffAccessBanner/TariffAccessBanner";
import { useRequestList } from "../../hooks/useRequestList";
import { useTariffAccess } from "../../hooks/useTariffAccess";
import "./RequestsSection.scss";

interface RequestsSectionProps {
  enabled?: boolean;
  showHeader?: boolean;
  embedded?: boolean;
  returnAdminTab?: AdminTabId;
}

export const RequestsSection = ({
  enabled = true,
  showHeader = true,
  embedded = false,
  returnAdminTab,
}: RequestsSectionProps) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { data, loading, error, refetch } = useRequestList({ page, pageSize, enabled });
  const tariffAccess = useTariffAccess();
  const createDisabled = !tariffAccess.canRunChecks;

  const handleRequestCreated = (result: { ids: string[]; openScheduleSetup?: boolean }) => {
    setShowCreateModal(false);
    refetch();

    if (result.ids.length !== 1) return;

    const id = result.ids[0];
    const path = result.openScheduleSetup ? `/request/${id}?openScheduleSetup=1` : `/request/${id}`;
    navigate(path, returnAdminTab ? { state: { adminTab: returnAdminTab } } : undefined);
  };

  if (!enabled) {
    return null;
  }

  return (
    <section
      className={`requests-section${embedded ? " requests-section--embedded" : ""}`}
      aria-label="Заявки"
    >
      {showHeader && (
        <div className="requests-section-header">
          <h1 className="requests-section-title">Мои сайты</h1>
          <button
            type="button"
            className="requests-section-create-btn"
            disabled={createDisabled}
            onClick={() => setShowCreateModal(true)}
          >
            <Plus size={20} />
            <span>Создать заявку</span>
          </button>
        </div>
      )}

      {tariffAccess.banner ? (
        <TariffAccessBanner banner={tariffAccess.banner} blockReason={tariffAccess.blockReason} />
      ) : null}

      {loading ? (
        <Shimmer rows={5} />
      ) : error ? (
        <div className="requests-section-error">
          <p className="requests-section-error__text">{error}</p>
          <button type="button" className="requests-section-error__retry" onClick={refetch}>
            Повторить
          </button>
        </div>
      ) : (
        <RequestTable
          data={data}
          page={page}
          pageSize={pageSize}
          returnAdminTab={returnAdminTab}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {showCreateModal && (
        <CreateRequestModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleRequestCreated}
        />
      )}
    </section>
  );
};
